import React, { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCcw } from "lucide-react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";
import { EventClickArg, DateSelectArg } from "@fullcalendar/core";
import { useUser } from "@/utils/userContext/UserContext";
import { useGlobalModal } from "@/components/GlobalModalProvider/GlobalModalProvider";
import { useBookings, BookingApi, BookableProduct } from "./hooks/useBookings";
import BookingModal from "./components/BookingModal";
import "./Agenda.css";

// Color palette for products
const PRODUCT_COLORS = [
  "#3b82f6", // blue
  "#10b981", // green
  "#f59e0b", // amber
  "#ef4444", // red
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#14b8a6", // teal
  "#f97316", // orange
];

function getProductColor(productId: string, products: BookableProduct[]): string {
  const index = products.findIndex((p) => p.id === productId);
  return PRODUCT_COLORS[index % PRODUCT_COLORS.length];
}

export default function Agenda() {
  const userContext = useUser();
  const token = userContext?.userData?.auth;
  const modal = useGlobalModal();
  const queryClient = useQueryClient();

  const {
    fetchBookings,
    fetchBookableProducts,
    createBooking,
    updateBooking,
    cancelBooking,
  } = useBookings();

  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalInitialData, setModalInitialData] = useState<{
    startTime: string;
    endTime: string;
    isFullDay?: boolean;
  } | undefined>();
  const [editingBooking, setEditingBooking] = useState<BookingApi | null>(null);

  // Calendar date range - initialize with current week to enable immediate fetch
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>(() => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const start = new Date(now);
    start.setDate(now.getDate() - dayOfWeek); // Start of week (Sunday)
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 7); // End of week
    end.setHours(23, 59, 59, 999);
    return { start, end };
  });

  // React Query for products
  const { data: products = [] } = useQuery<BookableProduct[]>({
    queryKey: ['bookableProducts'],
    queryFn: () => fetchBookableProducts(token!),
    enabled: !!token,
  });

  // Select all products by default when products load (from cache or fresh)
  useEffect(() => {
    if (products.length > 0 && selectedProducts.size === 0) {
      setSelectedProducts(new Set(products.map((p) => p.id)));
    }
  }, [products]);

  // React Query for bookings
  const { data: bookings = [], isLoading: loading, refetch: refetchBookings } = useQuery<BookingApi[]>({
    queryKey: ['bookings', dateRange.start.toISOString(), dateRange.end.toISOString()],
    queryFn: async () => {
      return await fetchBookings(
        token!,
        dateRange.start.toISOString(),
        dateRange.end.toISOString()
      );
    },
    enabled: !!token,
  });

  const [isRefreshing, setIsRefreshing] = useState(false);

  const invalidateBookings = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['bookings'] });
  }, [queryClient]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: ['bookings'] });
    await queryClient.invalidateQueries({ queryKey: ['bookableProducts'] });
    setIsRefreshing(false);
  };

  // Convert bookings to FullCalendar events
  const calendarEvents = bookings
    .filter((b) => selectedProducts.has(b.productId) && b.status !== "CANCELLED")
    .map((b) => ({
      id: b.id,
      title: `${b.product?.name || "Reserva"} - ${b.customer?.name || b.customer?.email || "Cliente"}`,
      start: b.startTime,
      end: b.endTime,
      allDay: b.isFullDay,
      backgroundColor: getProductColor(b.productId, products),
      borderColor: getProductColor(b.productId, products),
      extendedProps: { booking: b },
    }));

  // Handle date range change
  const handleDatesSet = (arg: { start: Date; end: Date }) => {
    setDateRange({ start: arg.start, end: arg.end });
  };

  // Handle date selection (create new booking)
  const handleDateSelect = (selectInfo: DateSelectArg) => {
    setEditingBooking(null);
    setModalInitialData({
      startTime: selectInfo.startStr,
      endTime: selectInfo.endStr,
      isFullDay: selectInfo.allDay,
    });
    setModalOpen(true);
  };

  // Handle event click (view/edit booking)
  const handleEventClick = (clickInfo: EventClickArg) => {
    const booking = clickInfo.event.extendedProps.booking as BookingApi;
    setModalInitialData(undefined);
    setEditingBooking(booking);
    setModalOpen(true);
  };

  // Handle save new booking
  const handleSaveBooking = async (data: {
    productId: string;
    customerId: string;
    startTime: string;
    endTime: string;
    isFullDay: boolean;
    notes?: string;
  }) => {
    if (!token) return;
    await createBooking(token, data);
    modal?.show({
      type: "success",
      title: "Sucesso",
      message: "Reserva criada com sucesso!",
      autoClose: 2500,
    });
    invalidateBookings();
  };

  // Handle update booking
  const handleUpdateBooking = async (
    bookingId: string,
    data: {
      startTime?: string;
      endTime?: string;
      isFullDay?: boolean;
      status?: string;
      notes?: string;
    }
  ) => {
    if (!token) return;
    await updateBooking(token, bookingId, data);
    modal?.show({
      type: "success",
      title: "Sucesso",
      message: "Reserva atualizada com sucesso!",
      autoClose: 2500,
    });
    invalidateBookings();
  };

  // Handle cancel booking
  const handleCancelBooking = async (bookingId: string) => {
    if (!token) return;
    await cancelBooking(token, bookingId);
    modal?.show({
      type: "success",
      title: "Sucesso",
      message: "Reserva cancelada com sucesso!",
      autoClose: 2500,
    });
    invalidateBookings();
  };

  // Toggle product filter
  const toggleProductFilter = (productId: string) => {
    setSelectedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  // Select/deselect all products
  const toggleAllProducts = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map((p) => p.id)));
    }
  };

  return (
    <div className="agenda-page">
      <header className="agenda-header">
        <div>
          <h1 className="agenda-title">Agenda</h1>
          <p className="agenda-sub dashboard-page-subtitle">
            Gerencie reservas de espaços e comodidades
          </p>
        </div>
        <div className="agenda-header-actions">
          <button
            className={`agenda-btn agenda-btn-refresh ${isRefreshing ? 'spinning' : ''}`}
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Atualizar dados"
          >
            <RefreshCcw size={16} />
            Atualizar
          </button>
          <button
            className="agenda-btn agenda-btn-primary"
            onClick={() => {
              setEditingBooking(null);
              setModalInitialData(undefined);
              setModalOpen(true);
            }}
          >
            + Nova Reserva
          </button>
        </div>
      </header>

      <div className="agenda-content">
        {/* Sidebar with product filters */}
        <aside className="agenda-sidebar">
          <div className="agenda-sidebar-header">
            <h3>Espaços</h3>
            <button
              className="agenda-filter-toggle"
              onClick={toggleAllProducts}
            >
              {selectedProducts.size === products.length
                ? "Desmarcar todos"
                : "Marcar todos"}
            </button>
          </div>
          <div className="agenda-product-list">
            {products.length === 0 ? (
              <p className="agenda-no-products">
                Nenhum espaço/comodidade cadastrado.
              </p>
            ) : (
              products.map((product) => (
                <label
                  key={product.id}
                  className="agenda-product-item"
                >
                  <input
                    type="checkbox"
                    checked={selectedProducts.has(product.id)}
                    onChange={() => toggleProductFilter(product.id)}
                  />
                  <span
                    className="agenda-product-color"
                    style={{
                      backgroundColor: getProductColor(product.id, products),
                    }}
                  />
                  <span className="agenda-product-name">{product.name}</span>
                </label>
              ))
            )}
          </div>
        </aside>

        {/* Calendar */}
        <div className="agenda-calendar-wrapper">
          {loading && <div className="agenda-loading">Carregando...</div>}
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay",
            }}
            locale={ptBrLocale}
            events={calendarEvents}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={true}
            weekends={true}
            select={handleDateSelect}
            eventClick={handleEventClick}
            datesSet={handleDatesSet}
            allDaySlot={true}
            slotMinTime="06:00:00"
            slotMaxTime="22:00:00"
            height="auto"
            stickyHeaderDates={true}
            nowIndicator={true}
            eventTimeFormat={{
              hour: "2-digit",
              minute: "2-digit",
              meridiem: false,
              hour12: false,
            }}
          />
        </div>
      </div>

      {/* Booking Modal */}
      <BookingModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingBooking(null);
          setModalInitialData(undefined);
        }}
        onSave={handleSaveBooking}
        onUpdate={handleUpdateBooking}
        onCancel={handleCancelBooking}
        products={products}
        initialData={modalInitialData}
        editingBooking={editingBooking}
      />
    </div>
  );
}

